import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { appEnv } from '../../../app.config';
import { ResponseModel } from '../../models/response.model';

export type CrmTaskModule = 'customer' | 'contractor' | 'carrier' | 'supplier' | 'ticket';
export type CrmTaskType = 'todo' | 'call' | 'email' | 'meeting' | 'follow_up';
export type CrmTaskPriority = 'none' | 'low' | 'medium' | 'high';

export const TASK_TYPES: { key: CrmTaskType; label: string; icon: string }[] = [
  { key: 'todo', label: 'To-do', icon: 'bi-check2-square' },
  { key: 'call', label: 'Call', icon: 'bi-telephone' },
  { key: 'email', label: 'Email', icon: 'bi-envelope' },
  { key: 'meeting', label: 'Meeting', icon: 'bi-people' },
  { key: 'follow_up', label: 'Follow-up', icon: 'bi-arrow-repeat' }
];
export const TASK_PRIORITIES: { key: CrmTaskPriority; label: string }[] = [
  { key: 'high', label: 'High' }, { key: 'medium', label: 'Medium' }, { key: 'low', label: 'Low' }, { key: 'none', label: 'None' }
];
export const TASK_MODULES: { key: CrmTaskModule; label: string; icon: string }[] = [
  { key: 'contractor', label: 'Contractor', icon: 'bi-person-badge' },
  { key: 'customer', label: 'Customer', icon: 'bi-building' },
  { key: 'carrier', label: 'Carrier', icon: 'bi-truck' },
  { key: 'supplier', label: 'Supplier', icon: 'bi-box-seam' },
  { key: 'ticket', label: 'Ticket', icon: 'bi-ticket-perforated' }
];

export interface CrmTask {
  uuid: string;
  /** null = a general to-do, not linked to a CRM record */
  module: CrmTaskModule | null;
  recordUuid: string | null;
  recordName: string | null;
  recordPath?: string | null;
  title: string;
  type?: CrmTaskType;
  priority?: CrmTaskPriority;
  description: string | null;
  assignedTo: number | null;
  assignedToName: string | null;
  dueAt: string | null;
  status: 'open' | 'done';
  completedAt: string | null;
  completedByName?: string | null;
  isOverdue?: boolean;
  createdBy: number | null;
  createdByName: string | null;
  fromWorkflow: boolean;
  createdAt: string | null;
  updatedAt?: string | null;
}

export interface CrmTaskFilters {
  scope?: 'mine' | 'all' | 'created' | 'unassigned';
  assignee?: number | null;
  status?: 'open' | 'done' | 'all';
  due?: 'overdue' | 'today' | 'week' | 'upcoming' | 'none' | 'any';
  type?: string;
  priority?: string;
  module?: string;
  /** only tasks on this record (uuid) */
  record?: string;
  q?: string;
  sort?: 'due' | 'priority' | 'created' | 'updated' | 'title' | 'completed';
  dir?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export interface CrmTaskSummary {
  open: number; overdue: number; dueToday: number; upcoming: number; noDueDate: number;
  completedThisWeek: number; highPriority: number; openTickets: number | null; ownedRecords: number | null;
  /** open tickets assigned to me - the sidebar's Service > Tickets badge */
  assignedTickets?: number | null;
}

export interface CrmWorkTicket { uuid: string; title: string; stage: string; priority: string; category: string | null; company: string | null; updatedAt: string; createdAt: string; path: string; openTasks?: number; }
export interface CrmWorkRecord { module: CrmTaskModule; uuid: string; name: string; sub: string | null; stage: string | null; updatedAt: string | null; path: string; openTasks?: number; }
export interface CrmRecordHit { uuid: string; name: string; sub: string | null; }

export type CrmTaskBody = Partial<{
  module: CrmTaskModule | null; record_uuid: string | null; record_name: string | null; title: string; type: CrmTaskType; priority: CrmTaskPriority;
  description: string | null; assigned_to: number | null; due_at: string | null; status: 'open' | 'done';
}>;

/** Server-side CRM tasks (crm/tasks) - a record's Tasks tab, My Tasks, and
 *  the tasks Workflow rules create. */
@Injectable({ providedIn: 'root' })
export class CrmTaskService {
  constructor (private http: HttpClient) {}

  forRecord (module: CrmTaskModule, recordUuid: string): Observable<CrmTask[]> {
    return this.http.get<ResponseModel>(`${appEnv.api}crm/tasks`, { params: { module, record: recordUuid } })
      .pipe(map(res => (res?.data as CrmTask[]) ?? []));
  }

  mine (status: 'open' | 'done' | 'all' = 'open'): Observable<CrmTask[]> {
    return this.http.get<ResponseModel>(`${appEnv.api}crm/tasks/mine`, { params: { status } })
      .pipe(map(res => (res?.data as CrmTask[]) ?? []));
  }

  create (body: CrmTaskBody & { title: string }): Observable<CrmTask> {
    return this.http.post<ResponseModel>(`${appEnv.api}crm/tasks`, body).pipe(map(res => res.data as CrmTask));
  }

  update (uuid: string, body: CrmTaskBody): Observable<CrmTask> {
    return this.http.patch<ResponseModel>(`${appEnv.api}crm/tasks/${uuid}`, body).pipe(map(res => res.data as CrmTask));
  }

  delete (uuid: string): Observable<boolean> {
    return this.http.delete<ResponseModel>(`${appEnv.api}crm/tasks/${uuid}`).pipe(map(res => !!res?.success));
  }

  /* ---- CRM > Tasks page ---- */

  list (filters: CrmTaskFilters): Observable<{ items: CrmTask[]; total: number; currentPage: number; lastPage: number }> {
    return this.http.get<ResponseModel>(`${appEnv.api}crm/tasks/list`, { params: this.params(filters) }).pipe(map(res => res.data));
  }

  summary (scope: 'mine' | 'all' = 'mine', assignee?: number | null, light = false): Observable<CrmTaskSummary> {
    return this.http.get<ResponseModel>(`${appEnv.api}crm/tasks/summary`, { params: this.params({ scope, assignee, light: light ? 1 : null }) }).pipe(map(res => res.data));
  }

  bulk (uuids: string[], action: 'complete' | 'reopen' | 'assign' | 'due' | 'priority' | 'type' | 'delete', value: any = null): Observable<number> {
    return this.http.post<ResponseModel>(`${appEnv.api}crm/tasks/bulk`, { uuids, action, value }).pipe(map(res => res?.data?.updated ?? 0));
  }

  searchRecords (module: CrmTaskModule, q: string): Observable<CrmRecordHit[]> {
    return this.http.get<ResponseModel>(`${appEnv.api}crm/tasks/records`, { params: { module, q } }).pipe(map(res => res?.data ?? []));
  }

  myWork (assignee?: number | null): Observable<{ tickets: CrmWorkTicket[]; records: CrmWorkRecord[] }> {
    return this.http.get<ResponseModel>(`${appEnv.api}crm/tasks/my-work`, { params: this.params({ assignee }) }).pipe(map(res => res.data));
  }

  private params (o: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    Object.entries(o).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') out[k] = String(v); });
    return out;
  }

  /** Route to open the record a task belongs to. */
  static recordLink (task: Pick<CrmTask, 'module' | 'recordUuid'>): string[] {
    if (!task.module || !task.recordUuid) return ['/crm/tasks'];
    switch (task.module) {
      case 'customer': return ['/crm/customer', task.recordUuid];
      case 'contractor': return ['/crm/contractor', task.recordUuid];
      case 'carrier': return ['/crm/carriers', task.recordUuid];
      case 'supplier': return ['/crm/supplier', task.recordUuid];
      default: return ['/crm/tickets'];
    }
  }
}
